/**
 * O recibo de custódia e a composição do material da abertura eletrônica (E126.F3, GUS-105,
 * ADR-0088 §2.7). Espelha `apps/api/types/abertura.py`.
 */
import type { AxiosInstance } from 'axios';

import type { SourceDocumentKind } from '@/lib/case_source_documents';
import type { OcorrenciaDaCustodiaData } from '@/lib/custodia';
import type { Modalidade, ModoDoExame } from '@/lib/cases';
import { classifyExameEletronicoError } from '@/lib/exame_eletronico';
import type { MotivoNaoExaminado } from '@/lib/recebimentos';

export type TipoDaComposicao =
  | 'documental'
  | 'com_biometria'
  | 'com_fotos'
  | 'com_biometria_e_fotos';

export type ProximaAcaoTecnica =
  | 'conferir_vinculo_biometrico'
  | 'solicitar_registro_biometrico'
  | 'solicitar_protocolo'
  /** E140.F2 (GUS-143): a leitura está em conferência; nada é pedido ao cliente antes. */
  | 'conferir_a_leitura';

/** E134.F1 (GUS-159, ADR-0090): a situação do objeto na conferência mais recente do caso. */
export type SituacaoNaConferencia = 'confere' | 'diverge' | 'ausente' | 'sem_registro';

/** E134.F1: como o derivado saiu do pai. */
export type TransformacaoDoDerivado = 'membro_do_pacote' | 'paginas_separadas' | 'imagem_da_pagina';

export interface SituacaoConferidaData {
  situacao: SituacaoNaConferencia;
  conferido_em: string;
}

/** E134.F1: a conferência mais recente do caso, com as contagens. */
export interface ResumoDaConferenciaData {
  conferencia_id: string;
  origem: 'tela' | 'leitura' | 'operacao';
  conferido_em: string;
  total: number;
  conferem: number;
  divergem: number;
  ausentes: number;
  sem_registro: number;
}

export interface DocumentoDoMaterialData {
  documento_id: string;
  nome: string;
  tipo: SourceDocumentKind;
  /** E134.F1: no derivado, o hash, o tamanho, a transformação e as páginas do pai. */
  sha256?: string | null;
  tamanho_bytes?: number | null;
  transformacao?: TransformacaoDoDerivado | null;
  paginas?: number[] | null;
  /** E134.F1: nula enquanto o objeto não foi conferido. */
  conferencia?: SituacaoConferidaData | null;
}

export interface OriginalRecebidoData extends DocumentoDoMaterialData {
  sha256: string | null;
  tamanho_bytes: number;
  recebido_em: string;
  recebido_por: string | null;
  derivados: DocumentoDoMaterialData[];
  /** E133.F1: o envio em que chegou; nulo no original anterior ao registro do envio. */
  recebimento_id: string | null;
}

/** E133.F1 (ADR-0089): o arquivo recebido que o exame não lê, guardado e marcado. */
export interface NaoExaminadoNoReciboData {
  item_id: string;
  nome: string;
  motivo: MotivoNaoExaminado;
  sha256: string;
  tamanho_bytes: number;
  recebido_em: string;
  /** O pacote de onde saiu; nulo quando chegou solto. */
  pacote_document_id: string | null;
  conferencia?: SituacaoConferidaData | null;
}

/** E135.F1 (GUS-158, ADR-0091): o que a plataforma fez com cada arquivo que chegou. */
export type SituacaoDaOcorrencia =
  | 'pacote'
  | 'examinado'
  | 'nao_examinado'
  | 'copia_identica'
  | 'ruido_do_sistema';

/** E135.F1: um arquivo que chegou no recebimento; o ruído do sistema não tem hash (não é lido). */
export interface OcorrenciaNoReciboData {
  ocorrencia_id: string;
  pai_id: string | null;
  caminho: string;
  tamanho_bytes: number;
  sha256: string | null;
  tipo_real: string | null;
  situacao: SituacaoDaOcorrencia;
  documento_id: string | null;
  motivo: string | null;
}

/** E135.F1: o manifesto selado da plataforma; sem assinatura nem carimbo (nenhum perfil aprovado). */
export interface ManifestoNoReciboData {
  manifesto_id: string;
  sha256: string;
  selado_em: string;
  anterior_id: string | null;
  ancora: 'nao_aplicada';
}

/** E133.F1: um envio, com os originais (por id) e os não examinados que trouxe. O grupo sem
 * `recebimento_id` reúne os originais anteriores ao registro do envio. */
export interface RecebimentoNoReciboData {
  recebimento_id: string | null;
  numero: number | null;
  recebido_em: string;
  recebido_por: string | null;
  originais: string[];
  nao_examinados: NaoExaminadoNoReciboData[];
  /** E135.F1: selado ao fim do envio; nulo enquanto aberto. */
  concluido_em?: string | null;
  manifesto?: ManifestoNoReciboData | null;
  ocorrencias?: OcorrenciaNoReciboData[];
  /** E177.F4 (ADR-0130 §2.2): a CCB declarada em que o envio entrou; nulo no envio sem CCB. */
  ccb_da_abertura_id?: string | null;
}

/** Derivado cuja origem saiu do caso: hash e instante são dele, não do arquivo recebido. */
export interface DerivadoSemOrigemData extends DocumentoDoMaterialData {
  origem_ausente_id: string;
  sha256: string | null;
  tamanho_bytes: number;
  gerado_em: string;
  derivados: DocumentoDoMaterialData[];
}

export interface ReciboDaCustodiaData {
  recebido_em: string | null;
  originais: OriginalRecebidoData[];
  derivados_sem_origem: DerivadoSemOrigemData[];
  alcance: 'desde_o_recebimento';
  recebimentos: RecebimentoNoReciboData[];
  /** E134.F1: a conferência mais recente do caso; nula se nunca foi conferido. */
  ultima_conferencia?: ResumoDaConferenciaData | null;
  /** E136.F1: as ocorrências de custódia do caso, da mais antiga para a mais recente. */
  ocorrencias?: OcorrenciaDaCustodiaData[];
}

export interface ComposicaoDoMaterialData {
  tipo: TipoDaComposicao;
  contratos: DocumentoDoMaterialData[];
  tem_protocolo: boolean;
  biometria_presente: boolean;
  fontes_da_biometria: DocumentoDoMaterialData[];
  par_de_fotos: boolean;
  fontes_das_fotos: string[];
  em_leitura: boolean;
  proxima_acao: ProximaAcaoTecnica;
}

/** E144.F1 (GUS-105, ADR-0098 §2.1): de onde saiu a chave do contrato. */
export type FonteDoIdentificador =
  | 'ade'
  | 'numero_da_cedula'
  | 'sistema'
  | 'proposta'
  | 'envelope'
  | 'dossie'
  | 'numero_do_contrato'
  | 'proposta_do_manifesto';

export interface IdentificadorDoContratoData {
  valor: string;
  fonte: FonteDoIdentificador;
}

/** E144.F1 (ADR-0098 §2.3): o contrato contra os casos que já existem. */
export type SituacaoDoContrato = 'novo' | 'complemento' | 'ja_recebido' | 'caso_do_escritorio';

export interface UnidadeDoEnvioData {
  documento_id: string;
  nome: string;
  /**
   * E177.F3.C03 (GUS-253 itens 5 e 6): o nome com que esta CCB recebeu a cópia idêntica de um
   * arquivo de outra CCB; `nome` segue o do arquivo de origem. Ausente na resposta antiga.
   */
  recebida_como?: string | null;
}

export interface ContratoDoEnvioData {
  /** Estável entre leituras; é o que a resposta da pergunta cita. */
  chave: string;
  nome: string;
  identificador: IdentificadorDoContratoData | null;
  contratos: string[];
  unidades: UnidadeDoEnvioData[];
  apoios: UnidadeDoEnvioData[];
  identificado: boolean;
  separacao_reprovada: boolean;
  situacao: SituacaoDoContrato;
  caso: CasoEncontradoData | null;
  /**
   * E165.F1.T06 (ADR-0114 §2): as escolhas cabíveis para a CCB que já tem caso — vincular ou abrir
   * novo no caso aberto, só a nova análise no fechado. Vazio (ou ausente) sem o que escolher.
   */
  opcoes?: EscolhaDoContrato[];
  /**
   * E177.F3 (ADR-0130 §2.4): a CCB declarada de que a linha saiu — as unidades são as de origem
   * dela, e os apoios, as vinculadas. Nulo (ou ausente) no envio sem CCB declarada.
   */
  ccb?: CcbDoContratoData | null;
}

/** E177.F3 (ADR-0130 §2.6): a chave de mesma natureza lida no pacote que não coincide. */
export interface DivergenciaDaCcbData {
  lida: IdentificadorDoContratoData;
  mantida: boolean;
}

export interface CcbDoContratoData {
  id: string;
  numero: string;
  ordem: number;
  divergencia: DivergenciaDaCcbData | null;
}

/** E165.F1.T06: `link` vincula ao caso existente; `new` abre caso novo com origem nele. */
export type EscolhaDoContrato = 'link' | 'new';

export interface CasoEncontradoData {
  case_id: string;
  codigo: string;
  /** E165.F1.T06: o laudo final assinado ou o caso encerrado. Ausente em resposta antiga. */
  fechado?: boolean;
}

export interface PerguntaDoApoioData {
  unidade: UnidadeDoEnvioData;
  pode_ser_contrato: boolean;
}

/** E144.F1 (ADR-0098 §2.2): os contratos do envio, os apoios e as perguntas. */
export interface EnvioDaAberturaData {
  contratos: ContratoDoEnvioData[];
  perguntas: PerguntaDoApoioData[];
  em_verificacao: boolean;
}

/** Respostas da pergunta: a chave de um contrato, ou uma destas. */
export const RESPOSTA_TODAS = 'todas';
export const RESPOSTA_OUTRO_CONTRATO = 'outro_contrato';

export interface AberturaDoCasoData {
  case_id: string;
  /** E133.F1: o código do exame (`GT-AAAA-NNNN`), que o recibo mostra como ID do caso. */
  codigo: string;
  modalidade: Modalidade;
  pasta: string | null;
  process_number: string | null;
  recibo: ReciboDaCustodiaData;
  composicao: ComposicaoDoMaterialData;
  /** E144.F1: só na eletrônica; ausente em resposta antiga. */
  envio?: EnvioDaAberturaData | null;
  /** E144.F1 (ADR-0098 §2.5): o modo do exame. */
  modo_exame?: ModoDoExame;
  /**
   * E165.F1.T04 (ADR-0114 §1): o CPF informado contra o lido nos documentos vivos (E165.F1.C03);
   * ausente sem CPF informado e enquanto o material é lido. Só a divergência trava a conclusão.
   */
  conferencia_do_cpf?: EstadoDoCpf | null;
  /** O CPF informado com só os dois últimos dígitos (`•••.•••.•••-NN`). */
  cpf_informado_mascarado?: string | null;
}

export type EstadoDoCpf = 'compativel' | 'divergente' | 'nao_conferido';

function ehContrato(data: unknown): data is AberturaDoCasoData {
  const corpo = data as Partial<AberturaDoCasoData> | null;
  return (
    typeof corpo === 'object' &&
    corpo !== null &&
    typeof corpo.recibo === 'object' &&
    Array.isArray(corpo.recibo?.originais) &&
    typeof corpo.composicao === 'object' &&
    Array.isArray(corpo.composicao?.contratos)
  );
}

export async function lerAberturaDoCaso(
  client: AxiosInstance,
  caseId: string,
): Promise<AberturaDoCasoData> {
  let data: unknown;
  try {
    ({ data } = await client.get<AberturaDoCasoData>(`/casos/${caseId}/abertura`));
  } catch (err) {
    throw classifyExameEletronicoError(err);
  }
  if (!ehContrato(data)) throw classifyExameEletronicoError(new Error('resposta fora do contrato'));
  return data;
}

/** Quem enviou, sem repetir: normalmente uma conta só. Conta os envios, que trazem também quem
 * mandou só arquivos não examinados. */
export function operadoresDoRecibo(recibo: ReciboDaCustodiaData): string[] {
  const nomes = [
    ...recibo.originais.map((o) => o.recebido_por),
    ...recibo.recebimentos.map((r) => r.recebido_por),
  ].filter((n): n is string => n !== null);
  return [...new Set(nomes)];
}

/** Todos os arquivos guardados sem exame, de todos os envios. */
export function naoExaminadosDoRecibo(recibo: ReciboDaCustodiaData): NaoExaminadoNoReciboData[] {
  return recibo.recebimentos.flatMap((r) => r.nao_examinados);
}

/** E135.F2.C02 (F-E135.F2-02): os envios com arquivo inventariado e sem conclusão, o mesmo
 * critério com que a emissão sela os abertos. O envio anterior ao inventário não entra: não há o
 * que selar nele. */
export function recebimentosAbertos(recibo: ReciboDaCustodiaData): string[] {
  return recibo.recebimentos.flatMap((r) =>
    r.recebimento_id !== null && !r.concluido_em && (r.ocorrencias?.length ?? 0) > 0
      ? [r.recebimento_id]
      : [],
  );
}

/** Quantos arquivos o envio trouxe: os originais e os guardados sem exame. */
export function arquivosDoRecebimento(recebimento: RecebimentoNoReciboData): number {
  return recebimento.originais.length + recebimento.nao_examinados.length;
}

/** O começo do ID do envio, para a linha (o inteiro fica no título). */
export function idCurto(id: string): string {
  return id.slice(0, 8);
}

/** Os arquivos de onde a composição foi identificada, sem repetir. */
export function fontesDaComposicao(composicao: ComposicaoDoMaterialData): string[] {
  return [
    ...new Set([
      ...composicao.contratos.map((c) => c.nome),
      ...composicao.fontes_da_biometria.map((f) => f.nome),
      ...composicao.fontes_das_fotos,
    ]),
  ];
}

/** O SHA-256 abreviado para a linha do arquivo (o inteiro fica no título, ao passar o cursor). */
export function hashCurto(sha256: string | null): string | null {
  return sha256 === null ? null : `${sha256.slice(0, 12)}…${sha256.slice(-6)}`;
}
